import React, { useContext, useState } from "react";
import "./ChoiceSteps.scss";
import { useIntl } from "react-intl";
import { configStore } from "../../../contexts/ConfigContext";

import { Image } from "@evidenceb/athena-common/modules/Assets";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { commonMessages, pageTitles } from "../../../utils/messages";
import classNames from "classnames";
import MiniLoader from "../../../components/Loader/MiniLoader";
import PageWrapper from "../../../components/PageWrapper/PageWrapper";
import useAssetsDetails from "../../../hooks/useAssetsDetails";
import Selection from "./Selection/Selection";
import ButtonNext from "../ButtonNext/ButtonNext";
import ContentWrapper from "../ContentWrapper/ContentWrapper";
import { useHistory } from "react-router-dom";
import Error from "../../../design-system-components/Error/Error";
import { subjectChoiceMessages } from "../messages";
import TabTitle from "../../../components/TabTitle/TabTitle";
import { useSubjects } from "../../../hooks/useActiveSubject";
import { useSubjectChoice } from "./api/useSubjectChoice";

const ChoiceSteps = () => {
    const intl = useIntl();
    const assetsDetails = useAssetsDetails();
    const history = useHistory();
    const {
        config: { placeholderImages, contact },
    } = useContext(configStore);
    const { isTabletOrSmaller } = useBreakpoints();
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

    const [currentSubjectChoice, setCurrentSubjectChoice] = useState<string>();
    const [_, { mutateAsync, isLoading, isError }] = useSubjectChoice();
    const subjects = useSubjects();

    const onValidation = async () => {
        if (!currentSubjectChoice) return;

        try {
            await mutateAsync(currentSubjectChoice);
            setCurrentStepIndex(currentStepIndex + 1);
        } catch (err) {
            console.log("onSubjectChoice ERROR", err);
        }
    };

    const onChoiceSelection = (subject: string | undefined) => {
        if (!subject) return;
        setCurrentSubjectChoice(subject);
    };

    const steps: React.ReactNode[] = [
        <>
            <div
                className={classNames({
                    "--is-tablet-or-smaller": isTabletOrSmaller,
                })}
            >
                <h2>
                    {intl.formatMessage(subjectChoiceMessages.subjectTitle)}
                </h2>
                <p>
                    {intl.formatMessage(subjectChoiceMessages.subjectMessage)}
                </p>

                <Selection
                    currentSubjectChoice={currentSubjectChoice}
                    onChoiceSelection={onChoiceSelection}
                    choices={subjects}
                    isLoading={isLoading}
                />
                {isError && (
                    <Error
                        className="choice-steps__error"
                        message={
                            <>
                                {intl.formatMessage(
                                    subjectChoiceMessages.errorValidation
                                )}
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href={`mailto:${contact}`}
                                >
                                    {intl.formatMessage(
                                        subjectChoiceMessages.errorContactSupport
                                    )}
                                </a>
                            </>
                        }
                        icon="warning"
                        color="var(--element-teacher-default)"
                    />
                )}
                {currentSubjectChoice &&
                    (isLoading ? (
                        <div className="choice-steps__loading">
                            <MiniLoader />
                        </div>
                    ) : (
                        <ButtonNext
                            className="choice-steps__btn-next"
                            label={intl.formatMessage(commonMessages.next)}
                            onClick={onValidation}
                        />
                    ))}
            </div>
        </>,

        <>
            <div
                className={classNames({
                    "--is-tablet-or-smaller": isTabletOrSmaller,
                })}
            >
                <h2>{intl.formatMessage(subjectChoiceMessages.thanksTitle)}</h2>
                <p>{intl.formatMessage(subjectChoiceMessages.thanksMessage)}</p>
                {isTabletOrSmaller && (
                    <div className="subject-choice__img-container">
                        <Image
                            src={placeholderImages?.teacher?.subjectChoice?.[1]}
                            alt=""
                            assetsDetails={assetsDetails}
                        />
                    </div>
                )}

                {isLoading ? (
                    <div className="choice-steps__loading">
                        <MiniLoader />
                    </div>
                ) : (
                    <ButtonNext
                        label={intl.formatMessage(commonMessages.start)}
                        onClick={() => history.push("/")}
                    />
                )}
            </div>
            {!isTabletOrSmaller && (
                <div>
                    <Image
                        src={placeholderImages?.teacher?.subjectChoice?.[1]}
                        alt=""
                        assetsDetails={assetsDetails}
                    />
                </div>
            )}
        </>,
    ];

    return (
        <>
            <TabTitle title={pageTitles.teacherSubjectChoice} />
            <PageWrapper className="choice-steps">
                {steps.map((step, i) => {
                    return (
                        <ContentWrapper
                            key={"step-" + i}
                            className={classNames("choice-steps__step", {
                                "--show": i === currentStepIndex,
                                "--step-1": i === 0,
                            })}
                        >
                            {step}
                        </ContentWrapper>
                    );
                })}
            </PageWrapper>
        </>
    );
};

export default ChoiceSteps;
